// YWS'S WORKING HOURS: THE ONE CLOCK FOR HOW LONG A WAIT HAS BEEN. Waits keep
// both timestamps, so working hours came as a toggle, not a rewrite.
//
// ⭐ PURE, AND THE ONLY CLOCK. Every reader of a wait's length asks
// `workingSeconds` (Response Times' lines, tiles and buckets, the Client
// Inbox's chips and "waited"), and so do the re-checks of quiet items (X4:
// `workingDays`, `isWorkingTime`) and the slow-reply tiers (X6). A client
// message at 4:30pm Friday answered at 9:30am Monday waited 1 hour, not 65.
// "3h ago" is not a wait: when something happened stays on the wall clock.
//
// The hours are an admin's setting (lib/server/working-hours.ts reads the
// four WORKING_* feature flags); the default is Mon–Fri 9am–5pm New York.
// Days and hours are the zone's own, so DST is the zone's: a working day is
// its local opening to its local closing, whatever the offset that day.
// `ALL_HOURS` (every day, midnight to midnight) is the wall clock, the same
// function: `workingSeconds(a, b, ALL_HOURS) === b - a`.
//
// Holidays are not in it (X7: out of scope unless trivial; a date list an
// admin keeps is a setting of its own, and nothing here would change but
// `windowOf`).
//
// The TikTok pull's times are read off the same setting (`pullSlots`, below).
//
// Every time here is in SECONDS (Slack's), as every wait's is.

export interface WorkingHours {
  /** The IANA zone the days and hours are in. */
  tz: string;
  /** The days worked, 0 = Sunday … 6 = Saturday. */
  days: readonly number[];
  /** Opening and closing, in minutes after local midnight: 0 ≤ open < close ≤ 1440. */
  open: number;
  close: number;
}

export const DEFAULT_WORKING_HOURS: WorkingHours = { tz: "America/New_York", days: [1, 2, 3, 4, 5], open: 9 * 60, close: 17 * 60 };

/** Every hour of every day: the wall clock, as a setting. */
export const ALL_HOURS: WorkingHours = { tz: "UTC", days: [0, 1, 2, 3, 4, 5, 6], open: 0, close: 24 * 60 };

const DAY_S = 86_400;

/** Every hour of every day: the wall clock. */
export const isAllHours = (h: WorkingHours): boolean => h.open === 0 && h.close === 24 * 60 && [0, 1, 2, 3, 4, 5, 6].every((d) => h.days.includes(d));

// ─── The zone's calendar ──────────────────────────────────────────────────

const partsFmt = new Map<string, Intl.DateTimeFormat>();
function fmtOf(tz: string): Intl.DateTimeFormat {
  let f = partsFmt.get(tz);
  if (!f) {
    f = new Intl.DateTimeFormat("en-US", { timeZone: tz, hourCycle: "h23", year: "numeric", month: "numeric", day: "numeric", hour: "numeric", minute: "numeric", second: "numeric" });
    partsFmt.set(tz, f);
  }
  return f;
}

/** The zone's wall time at `t`, read as if it were UTC, in seconds: local minus UTC is the offset. */
function localAsUtc(tz: string, t: number): number {
  const p: Record<string, number> = {};
  for (const x of fmtOf(tz).formatToParts(new Date(Math.floor(t) * 1000))) if (x.type !== "literal") p[x.type] = Number(x.value);
  return Date.UTC(p.year, p.month - 1, p.day, p.hour % 24, p.minute, p.second) / 1000;
}

/** The local day `t` falls on, as whole days since 1970-01-01 (the zone's calendar). */
export function localDay(tz: string, t: number): number {
  return Math.floor(localAsUtc(tz, t) / DAY_S);
}

/** The moment, in seconds, that local day `day` reads `minute` minutes past midnight. */
export function localMoment(tz: string, day: number, minute: number): number {
  const guess = day * DAY_S + minute * 60;
  const o1 = localAsUtc(tz, guess) - guess;
  const r = guess - o1;
  const o2 = localAsUtc(tz, r) - r;
  // Across a DST change the offset at the answer is the one that counts.
  return o2 === o1 ? r : guess - o2;
}

interface Window {
  /** The local day's midnight, in seconds. */
  start: number;
  /** Its working hours, or null on a day not worked. */
  open: number | null;
  close: number | null;
}

const windows = new Map<string, Map<number, Window>>();

/** The setting's cache of local days, one per distinct setting (a 90-day read asks the same days thousands of times). */
function daysOf(h: WorkingHours): Map<number, Window> {
  const k = `${h.tz}|${h.open}|${h.close}|${[...h.days].sort().join("")}`;
  let byDay = windows.get(k);
  if (!byDay) {
    byDay = new Map();
    windows.set(k, byDay);
  }
  return byDay;
}

/** Local day `day`'s midnight and working window. */
function windowOf(h: WorkingHours, byDay: Map<number, Window>, day: number): Window {
  let w = byDay.get(day);
  if (!w) {
    const worked = h.days.includes(new Date(day * DAY_S * 1000).getUTCDay());
    w = { start: localMoment(h.tz, day, 0), open: worked ? localMoment(h.tz, day, h.open) : null, close: worked ? localMoment(h.tz, day, h.close) : null };
    byDay.set(day, w);
  }
  return w;
}

// ─── The clock ────────────────────────────────────────────────────────────

/** The working time from `a` to `b`, in seconds; 0 when `b` isn't later. */
export function workingSeconds(a: number, b: number, h: WorkingHours): number {
  if (!(b > a)) return 0;
  if (isAllHours(h)) return b - a;
  const byDay = daysOf(h);
  let s = 0;
  for (let day = localDay(h.tz, a); ; day++) {
    const w = windowOf(h, byDay, day);
    if (w.start >= b) break;
    if (w.open === null || w.close === null) continue;
    s += Math.max(0, Math.min(b, w.close) - Math.max(a, w.open));
  }
  return s;
}

/** Is `t` inside working hours (from opening, up to but not at closing)? */
export function isWorkingTime(t: number, h: WorkingHours): boolean {
  const w = windowOf(h, daysOf(h), localDay(h.tz, t));
  return w.open !== null && w.close !== null && t >= w.open && t < w.close;
}

/** A working day's length, in seconds: 8 hours for 9–5; a whole day for ALL_HOURS. */
export const workingDayLength = (h: WorkingHours): number => (h.close - h.open) * 60;

/** The working time from `a` to `b` in working days (X4's "quiet ≥ 5 working days" is 40 working hours on 9–5). */
export const workingDays = (a: number, b: number, h: WorkingHours): number => workingSeconds(a, b, h) / workingDayLength(h);

// ─── The TikTok pull's times ──────────────────────────────────────────────
//
// The pull runs at times taken from the setting, not every hour, so TikTok
// sees the shape of a working day rather than a machine's clock: the first
// PULL_LEAD_MIN before opening (the requests are read by the time someone sits
// down to them), then every PULL_EVERY_MIN while the time is before closing,
// on working days only. The default gives 7am, 10am, 1pm and 4pm New York time,
// Monday to Friday, and no weekend pull: Monday's first carries the weekend. An
// opening earlier than the lead starts at midnight, so ALL_HOURS is every three
// hours from midnight, every day.

/** The first pull of a working day, in minutes before opening. */
export const PULL_LEAD_MIN = 120;
/** The gap between a day's pulls, in minutes. */
export const PULL_EVERY_MIN = 180;

/** Local day `day`'s pull times, in seconds, earliest first; none on a day not worked. */
export function pullSlots(h: WorkingHours, day: number): number[] {
  if (!h.days.includes(new Date(day * DAY_S * 1000).getUTCDay())) return [];
  const out: number[] = [];
  for (let m = Math.max(0, h.open - PULL_LEAD_MIN); m < h.close; m += PULL_EVERY_MIN) out.push(localMoment(h.tz, day, m));
  return out;
}

/** The first pull time after `t`, in seconds; null for a setting with no working day. */
export function nextPullSlot(h: WorkingHours, t: number): number | null {
  const d = localDay(h.tz, t);
  for (let day = d; day <= d + 8; day++) for (const s of pullSlots(h, day)) if (s > t) return s;
  return null;
}

/** A pull time and the end of its window: the next pull time, or PULL_EVERY_MIN on, whichever is first. */
export interface PullSlot {
  at: number;
  until: number;
}

/**
 * The pull time whose window `t` is in, or null between windows (a night, a
 * weekend). ⚠️ A WINDOW, NOT "THE LATEST TIME": a 4pm pull missed to a deploy
 * is caught up by 7pm, never at 3am on Saturday.
 */
export function pullSlotAt(h: WorkingHours, t: number): PullSlot | null {
  const d = localDay(h.tz, t);
  let at: number | null = null;
  for (let day = d; day >= d - 7 && at === null; day--) {
    const past = pullSlots(h, day).filter((s) => s <= t);
    if (past.length > 0) at = past[past.length - 1];
  }
  if (at === null) return null;
  const until = Math.min(at + PULL_EVERY_MIN * 60, nextPullSlot(h, at) ?? Infinity);
  return t < until ? { at, until } : null;
}

/** The longest wait between two pulls, in seconds, over a fortnight of no DST change: Friday 4pm to Monday 7am on the default. */
export function longestPullGap(h: WorkingHours): number {
  const from = Date.UTC(2026, 0, 5) / 1000 / DAY_S;
  const all: number[] = [];
  for (let day = from; day < from + 21; day++) all.push(...pullSlots(h, day));
  let gap = 0;
  for (let i = 1; i < all.length; i++) gap = Math.max(gap, all[i] - all[i - 1]);
  return all.length < 2 ? 7 * DAY_S : gap;
}

const WEEKDAYS = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

/** A pull time as people say it, in the setting's zone: "7am New York time", "tomorrow 7am New York time", "Monday 7am New York time". */
export function pullTimeWords(at: number, now: number, h: WorkingHours): string {
  const local = localAsUtc(h.tz, at);
  // The setting is in whole hours, so every pull time is on the hour.
  const time = hourWord(Math.floor((local % DAY_S) / 3600));
  const zone = WORKING_ZONES.find((z) => z.value === h.tz)?.label ?? h.tz;
  const days = Math.floor(local / DAY_S) - localDay(h.tz, now);
  const day = days === 0 ? "" : days === 1 ? "tomorrow " : `${WEEKDAYS[new Date(Math.floor(local / DAY_S) * DAY_S * 1000).getUTCDay()]} `;
  return `${day}${time} ${zone} time`;
}

// ─── The setting, read and said ───────────────────────────────────────────

/** The feature flags that hold it (lib/feature-flags.ts), by part. */
export const WORKING_HOURS_FLAGS = { days: "WORKING_DAYS", open: "WORKING_HOURS_OPEN", close: "WORKING_HOURS_CLOSE", tz: "WORKING_HOURS_ZONE" } as const;

/** The day sets an admin may pick, as the flag stores them. */
export const WORKING_DAY_SETS: ReadonlyArray<{ value: string; label: string; days: readonly number[] }> = [
  { value: "mon-fri", label: "Monday to Friday", days: [1, 2, 3, 4, 5] },
  { value: "mon-sat", label: "Monday to Saturday", days: [1, 2, 3, 4, 5, 6] },
  { value: "every-day", label: "Every day", days: [0, 1, 2, 3, 4, 5, 6] },
];

/** The zones an admin may pick. */
export const WORKING_ZONES: ReadonlyArray<{ value: string; label: string }> = [
  { value: "America/New_York", label: "New York" },
  { value: "America/Chicago", label: "Chicago" },
  { value: "America/Denver", label: "Denver" },
  { value: "America/Los_Angeles", label: "Los Angeles" },
  { value: "Europe/London", label: "London" },
  { value: "UTC", label: "UTC" },
];

/** An hour of the day as people say it: 12am, 9am, 12pm, 5pm; 24 is midnight. */
export const hourWord = (hour: number): string => (hour % 24 === 0 ? (hour === 24 ? "midnight" : "12am") : hour < 12 ? `${hour}am` : hour === 12 ? "12pm" : `${hour - 12}pm`);

/**
 * The setting from the four flags' values (each undefined when unset, then the
 * default's). A value the page couldn't have stored, or a closing not after the
 * opening (one part cleared back to the default), reads as the whole default:
 * a clock that is half one setting and half another would be no one's.
 */
export function workingHoursFrom(read: (key: string) => string | undefined): WorkingHours {
  const days = read(WORKING_HOURS_FLAGS.days);
  const open = read(WORKING_HOURS_FLAGS.open);
  const close = read(WORKING_HOURS_FLAGS.close);
  const tz = read(WORKING_HOURS_FLAGS.tz);
  const d = DEFAULT_WORKING_HOURS;
  const set = days === undefined ? d.days : WORKING_DAY_SETS.find((s) => s.value === days.trim())?.days;
  const o = open === undefined ? d.open / 60 : Number(open);
  const c = close === undefined ? d.close / 60 : Number(close);
  const zone = tz === undefined ? d.tz : WORKING_ZONES.find((z) => z.value === tz.trim())?.value;
  if (set === undefined || zone === undefined || !Number.isInteger(o) || !Number.isInteger(c) || o < 0 || c > 24 || !(o < c)) return d;
  return { tz: zone, days: set, open: o * 60, close: c * 60 };
}

/** The setting in words: "Monday to Friday, 9am–5pm New York time". */
export function workingHoursText(h: WorkingHours): string {
  if (isAllHours(h)) return "every hour of every day";
  const set = WORKING_DAY_SETS.find((s) => s.days.length === h.days.length && s.days.every((x) => h.days.includes(x)));
  const days = set ? set.label : [...h.days].sort().map((x) => ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"][x]).join(", ");
  const zone = WORKING_ZONES.find((z) => z.value === h.tz)?.label ?? h.tz;
  return `${days}, ${hourWord(h.open / 60)}–${hourWord(h.close / 60)} ${zone} time`;
}
